import type { CSSProperties, ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DraggableAttributes,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { lockToVerticalAxis, transformToCss } from '../utils/sortableDrag';

type SortableListeners = ReturnType<typeof useSortable>['listeners'];

export interface DragHandleBindings {
  ref: (node: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: SortableListeners;
  disabled: boolean;
}

export interface SortableRenderProps {
  setNodeRef: (node: HTMLElement | null) => void;
  style: CSSProperties;
  isDragging: boolean;
  handleProps: DragHandleBindings;
}

interface SortableListProps {
  ids: string[];
  onMove: (activeId: string, overId: string) => void;
  onDragStart?: (activeId: string) => void;
  onDragEnd?: () => void;
  children: ReactNode;
}

export function SortableList({ ids, onMove, onDragStart, onDragEnd, children }: SortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    onDragStart?.(String(active.id));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && over.id !== active.id) onMove(String(active.id), String(over.id));
    onDragEnd?.();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[lockToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => onDragEnd?.()}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

interface SortableItemProps {
  id: string;
  disabled?: boolean;
  children: (sortable: SortableRenderProps) => ReactNode;
}

export function SortableItem({ id, disabled = false, children }: SortableItemProps) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: CSSProperties = {
    transform: transformToCss(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 20 } : {}),
  };
  return (
    <>
      {children({
        setNodeRef,
        style,
        isDragging,
        handleProps: { ref: setActivatorNodeRef, attributes, listeners, disabled },
      })}
    </>
  );
}

interface DragHandleProps {
  bindings: DragHandleBindings;
  label: string;
  disabledHint?: string;
  className?: string;
  iconSize?: number;
}

export function DragHandle({ bindings, label, disabledHint, className = '', iconSize = 18 }: DragHandleProps) {
  if (bindings.disabled) {
    return (
      <button
        type="button"
        aria-disabled="true"
        aria-label={label}
        title={disabledHint}
        className={`p-1.5 flex-shrink-0 text-gray-400 opacity-30 cursor-not-allowed ${className}`}
      >
        <GripVertical size={iconSize} />
      </button>
    );
  }
  return (
    <button
      type="button"
      ref={bindings.ref}
      {...bindings.attributes}
      {...bindings.listeners}
      aria-label={label}
      title="Drag to move"
      style={{ touchAction: 'none' }}
      className={`p-1.5 flex-shrink-0 text-gray-400 hover:text-primary cursor-grab active:cursor-grabbing ${className}`}
    >
      <GripVertical size={iconSize} />
    </button>
  );
}
